import { Injector, computed, inject, runInInjectionContext, signal } from '@angular/core';
import { IData } from '@core/models/IApi';
import {
    ColCateg,
    Column,
    ColumnForTable,
    SelectedCols,
    Sorting,
    TableInit,
} from '@features/social-analysis/models/ITable';
import { PAGE_DIR, PageDir } from '@features/social-analysis/utils/table-utils';
import { ArrayUtils } from '@shared/utils/Arrays';
import { ToastService } from '@shared/services/toast.service';
import { PostsHttpBuilder } from '../../social-listening/services/posts.http';
import { TagsMentionsHttpBuilder } from '../../social-users/services/tags-ment.http';
import { UsersHttpBuilder } from '../../social-users/services/users.http';
import { PageFiltersState } from './page-filters.store';
import { SingleSelectState } from './state.store';

export abstract class TableState<T = IData, K = T> extends SingleSelectState<T, K[]> {
    #toastr!: ToastService;

    component: string;
    columns: ColCateg[] = [];
    selectedCols = signal<SelectedCols>({});
    colsForTable = computed(() => this.computeDisplayCols());
    sorting = signal<Sorting[]>([]);
    hiddenSort?;
    total = signal<number>(0);
    offset = signal<number>(0);
    limit = signal<number>(20);
    override selected = signal<K[]>([]);

    constructor(
        { component, columns, selectedCols, sorting, hiddenSort }: TableInit,
        injector: Injector
    ) {
        super();
        this.component = component;
        this.columns = columns;
        this.selectedCols.set(selectedCols);
        this.sorting.set(sorting || []);
        this.hiddenSort = hiddenSort;

        runInInjectionContext(injector, () => (this.#toastr = inject(ToastService)));
    }

    setSort(newSort: Sorting) {
        const i = this.sorting().findIndex(sort => sort.prop === newSort.prop);

        if (i === -1) this.#addSort(newSort);
        else if (!newSort.dir) this.#deleteSort(i);
        else this.#updateSort(newSort, i);
    }

    #addSort(newSort: Sorting) {
        this.sorting.update(sort => [...sort, newSort]);
    }

    #updateSort(newSort: Sorting, i: number) {
        this.sorting.update(sort => [...ArrayUtils.returnSpliced(sort, i, 1, newSort)]);
    }

    #deleteSort(i: number) {
        this.sorting.update(sort => [...ArrayUtils.returnSpliced(sort, i, 1)]);
    }

    setPage(dir: PageDir) {
        if (!this.total()) return;
        if (!this.offset() && dir === PAGE_DIR.PREV)
            return this.#toastr.warning('Already at page 1');
        if (this.offset() + this.limit() >= this.total() && dir === PAGE_DIR.NEXT)
            return this.#toastr.warning('No more data to load');

        this.offset.update(state =>
            dir === PAGE_DIR.NEXT ? (state += this.limit()) : (state -= this.limit())
        );
    }

    protected computeDisplayCols(): ColumnForTable[] {
        const selectedCols = this.selectedCols();
        const allCols = this.columns.flatMap((categ: ColCateg) => categ.values);

        return allCols.reduce((acc: ColumnForTable[], curr: Column) => {
            if (selectedCols[curr.value] || curr.fixed)
                return [
                    ...acc,
                    {
                        prop: curr.value,
                        name: this.#getColDisplayValue(curr),
                        qtyType: curr.qtyType || '',
                        unsortable: curr?.unsortable,
                        fixWidth: curr?.fixWidth,
                    },
                ];
            else return acc;
        }, []);
    }

    #getColDisplayValue(curr: Column) {
        if (curr.value === 'product_image') return '';
        if (curr.value === 'ig_account_image') return '';
        if (curr.value === 'user_image') return '';
        return curr.display;
    }

    abstract reqData(pageFilters: PageFiltersState, needsTotal?: boolean): void;
    abstract reqTotal(pageFilters: PageFiltersState): void;
}

export class PostsTable extends TableState {
    #http!: PostsHttpBuilder;

    constructor(init: TableInit, injector: Injector) {
        super(init, injector);
        runInInjectionContext(injector, () => (this.#http = new PostsHttpBuilder()));
    }

    reqData(pageFilters: PageFiltersState, needsTotal?: boolean) {
        this.#http.reqData(this, pageFilters, needsTotal);
    }

    reqTotal(pageFilters: PageFiltersState) {
        this.#http.reqTotal(this, pageFilters);
    }
}

export class IgUsersTable extends TableState {
    #http!: UsersHttpBuilder;

    constructor(init: TableInit, injector: Injector) {
        super(init, injector);
        runInInjectionContext(injector, () => (this.#http = new UsersHttpBuilder()));
    }

    reqData(pageFilters: PageFiltersState) {
        this.#http.reqData(this, pageFilters);
    }

    reqTotal(pageFilters: PageFiltersState) {
        this.#http.reqTotal(this, pageFilters);
    }
}

export class TagsMentionsTable extends TableState {
    #http!: TagsMentionsHttpBuilder;

    constructor(init: TableInit, injector: Injector) {
        super(init, injector);
        runInInjectionContext(injector, () => (this.#http = new TagsMentionsHttpBuilder()));
    }

    reqData(pageFilters: PageFiltersState) {
        this.#http.reqData(this, pageFilters);
    }

    reqTotal(pageFilters: PageFiltersState) {
        this.#http.reqTotal(this, pageFilters);
    }
}
